/**
 * Cliente HTTP da API do MonteBox.
 *
 * Tres responsabilidades e nada mais: montar a URL, anexar o access token e
 * renovar a sessao quando ele expira. Cache, estado de tela e revalidacao sao
 * do TanStack Query — misturar as duas coisas e o que costuma virar um cliente
 * HTTP de 500 linhas que ninguem entende.
 */

const BASE_URL = (import.meta.env["VITE_API_URL"] ?? "http://localhost:3333").replace(/\/$/, "");

/** Raiz das rotas da API — para envios fora do `request` (ex.: `keepalive`). */
export const API_URL = `${BASE_URL}/api`;

/** Chave do par de tokens no localStorage. */
const STORAGE_KEY = "montebox:auth";

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Tokens em memoria, espelhados no localStorage.
 *
 * A copia em memoria evita ler o storage a cada requisicao e mantem a sessao
 * consistente entre abas do mesmo documento; o storage e o que faz a sessao
 * sobreviver ao reload.
 */
let tokens: AuthTokens | null = readTokens();

function readTokens(): AuthTokens | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as AuthTokens) : null;
  } catch {
    return null;
  }
}

export function getTokens(): AuthTokens | null {
  return tokens;
}

export function setTokens(next: AuthTokens | null): void {
  tokens = next;
  try {
    if (next) localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Navegacao anonima com storage bloqueado: a sessao vale so nesta aba.
  }
}

/** Avisa a aplicacao que a sessao caiu de vez (refresh recusado). */
type SessionListener = () => void;
const sessionLost = new Set<SessionListener>();

export function onSessionLost(listener: SessionListener): () => void {
  sessionLost.add(listener);
  return () => sessionLost.delete(listener);
}

/**
 * Renovacao compartilhada.
 *
 * Se cinco consultas expirarem ao mesmo tempo, todas esperam a MESMA promessa
 * de refresh. Sem isso, cinco chamadas de refresh disparam em paralelo e, como
 * o backend rotaciona o token a cada uso, quatro delas chegariam com um token
 * ja revogado e derrubariam a sessao.
 */
let refreshing: Promise<AuthTokens | null> | null = null;

async function refreshTokens(): Promise<AuthTokens | null> {
  const current = tokens;
  if (!current) return null;

  refreshing ??= (async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken: current.refreshToken }),
      });
      if (!response.ok) return null;
      return (await response.json()) as AuthTokens;
    } catch {
      return null;
    } finally {
      // Libera a proxima rodada so depois que esta terminou.
      queueMicrotask(() => {
        refreshing = null;
      });
    }
  })();

  const next = await refreshing;
  if (next) {
    setTokens(next);
  } else {
    setTokens(null);
    for (const listener of sessionLost) listener();
  }
  return next;
}

async function parseError(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { message?: string | string[] };
    if (Array.isArray(body.message)) return body.message.join("; ");
    if (body.message) return body.message;
  } catch {
    // Corpo vazio ou nao-JSON: cai na mensagem generica abaixo.
  }
  return `Erro ${response.status}`;
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  /** Envia o Authorization quando ha sessao (padrao: true). */
  auth?: boolean;
  signal?: AbortSignal;
  /** Devolve o corpo como arquivo (CSV...) em vez de JSON. */
  blob?: boolean;
}

async function request<T>(path: string, options: RequestOptions, retry = true): Promise<T> {
  const { method = "GET", body, auth = true, signal, blob = false } = options;

  // FormData (envio de arquivo) vai como está: o navegador monta o
  // Content-Type com o boundary do multipart, e defini-lo à mão o quebraria.
  const isForm = body instanceof FormData;
  const headers: Record<string, string> = {};
  if (body !== undefined && !isForm) headers["Content-Type"] = "application/json";
  if (auth && tokens) headers["Authorization"] = `Bearer ${tokens.accessToken}`;

  const response = await fetch(`${BASE_URL}/api${path}`, {
    method,
    headers,
    ...(body === undefined ? {} : { body: isForm ? body : JSON.stringify(body) }),
    ...(signal ? { signal } : {}),
  });

  // 401 com sessao: tenta renovar UMA vez e repete a requisicao original.
  if (response.status === 401 && auth && tokens && retry) {
    const next = await refreshTokens();
    if (next) return request<T>(path, options, false);
  }

  if (!response.ok) throw new ApiError(response.status, await parseError(response));
  if (response.status === 204) return undefined as T;
  if (blob) return (await response.blob()) as T;
  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string, options: Omit<RequestOptions, "method" | "body"> = {}) =>
    request<T>(path, { ...options, method: "GET" }),
  post: <T>(path: string, body?: unknown, options: Omit<RequestOptions, "method"> = {}) =>
    request<T>(path, { ...options, method: "POST", body }),
  put: <T>(path: string, body?: unknown, options: Omit<RequestOptions, "method"> = {}) =>
    request<T>(path, { ...options, method: "PUT", body }),
  patch: <T>(path: string, body?: unknown, options: Omit<RequestOptions, "method"> = {}) =>
    request<T>(path, { ...options, method: "PATCH", body }),
  /** Aceita corpo: desfazer algo às vezes exige dizer o motivo. */
  delete: <T>(path: string, options: Omit<RequestOptions, "method"> = {}) =>
    request<T>(path, { ...options, method: "DELETE" }),
  /**
   * Baixa um arquivo gerado pela API (CSV de relatório...).
   *
   * Um `<a href>` comum não serviria: a rota exige o token, e link não manda
   * cabeçalho. Aqui o arquivo vem pela mesma requisição autenticada — com a
   * mesma renovação de sessão — e é entregue ao navegador para salvar.
   */
  download: async (path: string, filename: string): Promise<void> => {
    const file = await request<Blob>(path, { method: "GET", blob: true });
    const url = URL.createObjectURL(file);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  },
  /** Envia um arquivo como `multipart/form-data`, no campo `file`. */
  upload: <T>(
    path: string,
    file: File,
    fields: Record<string, string> = {},
    options: Omit<RequestOptions, "method" | "body"> = {},
  ) => {
    const form = new FormData();
    for (const [name, value] of Object.entries(fields)) form.append(name, value);
    form.append("file", file);
    return request<T>(path, { ...options, method: "POST", body: form });
  },
};
